import createEngine, { DefaultPortModel, DiagramModel, NodeModel, } from '@projectstorm/react-diagrams';

import { DiagramEngine } from '@projectstorm/react-diagrams-core';
import { DagreEngine } from '@projectstorm/react-diagrams';

import { setDialog } from "../app/appSlice";
import { InputType } from '@projectstorm/react-canvas-core';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from './hooks';
import { slugify } from '../pages/editor/catalog/CatalogItem';
import { useCatalog } from './useCatalog';
import { DataNodeFactory } from '../pages/editor/diagram/nodes/DataNodeFactory';
import { DataNodeModel, DataNodeModelOptions, NodeKind } from '../pages/editor/diagram/nodes/DataNodeModel';
import { NodeLinkFactory } from '../pages/editor/diagram/links/NodeLinkFactory';
import { NodeLinkModel } from '../pages/editor/diagram/links/NodeLinkModel';
import deletedBoxes from "../config/deletedbox.json";
import { Button } from "primereact/button";
import {
  changeSidebarPane,
  NodeDefinition,
  NodeDefinitions,
  setFilter,
  SettingsValues,
  PortDefinition,
  Condition,
  SideOutPortDefinition,
} from '../pages/editor/editorSlice';
import { DataPortFactory } from '../pages/editor/diagram/ports/DataPortFactory';
import { DataSidePortFactory } from '../pages/editor/diagram/ports/DataSidePortFactory';
import CheckConditionValue from '../utils/checkConditionValue';
import { Point } from '@projectstorm/geometry';
import { setToast } from '../app/appSlice';
import { useNavigate } from 'react-router-dom';
import { RuntimeMode, useGetConfigQuery } from '../app/apiSlice';
import { useTranslation } from 'react-i18next';
import { Schema } from 'js-yaml';

// This store is shared between all useGraph() hooks
const store: {
  engine: DiagramEngine | undefined;
  activeNode: DataNodeModel | undefined;
  ref: any;
} = {
  engine: undefined,
  activeNode: undefined,
  ref: undefined,
};

export interface DagItem {
  id: string;
  type: string;
  asStream?: boolean;
  asTable?: boolean;
  out?: string[];

  [key: string]: any;
}

export const useGraph = () => {
  const { getSchema } = useCatalog();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const placeholderRegex = /\${\s*([^}]+)\s*}/g;
  const { data : config } = useGetConfigQuery();
  const {t: translate} = useTranslation();

  const createEmptyDiagram = (): DiagramEngine => {
    const engine = createEngine({
      registerDefaultZoomCanvasAction: false,
      registerDefaultDeleteItemsAction: false,
    });

    // register custom nodes styles
    const pF = engine.getPortFactories();
    pF.registerFactory(new DataPortFactory());
    pF.registerFactory(new DataSidePortFactory());

    engine.getNodeFactories().registerFactory(
      new DataNodeFactory({
        showSidePanel: () => {
          const selection = engine.getModel().getSelectedEntities();
          if (selection.length === 0) {
            // No node selected
          } else if (selection.length === 1) {
            // Only one node selected
            if (selection[0] && Object.prototype.hasOwnProperty.call(selection[0], "kind")) {
              store.activeNode = selection[0] as DataNodeModel;
              dispatch(changeSidebarPane({ pane: "configuration", arg: selection[0].getID() }));
            }
          } else {
            // Several nodes selected
            store.activeNode = undefined;
            dispatch(changeSidebarPane({ pane: "multiSelection", arg: selection }));
          }
        }
      })
    );

    // register custom link styles
    engine.getLinkFactories().registerFactory(new NodeLinkFactory());

    // disable breaking link by point (Shifh+click on link)
    engine.setMaxNumberPointsPerLink(0);

    // DiagramModel events
    // nodesUpdated
    // linksUpdated
    // offsetUpdated
    // zoomUpdated
    // gridUpdated
    // selectionChanged
    // entityRemoved

    engine.setModel(createModel());

    // engine.getActionEventBus().registerAction(new CustomDeleteItemsAction());

    // deregister all predefined action to edget effect (bind del key in all app...)
    const actions = engine
      .getActionEventBus()
      .getActionsForType(InputType.KEY_DOWN);
    for (const action of actions) {
      engine.getActionEventBus().deregisterAction(action);
    }

    return engine;
  };

  const removeLinkWithoutTargetPort = (model: any) => {
    const listLink = Object.values(model.getLinks());
    listLink.forEach((link: any) => {
      if (!link.targetPort) {
        model.removeLink(link);
      }
    });
  };

  const generateRandomName = (nodeName: string) => {
    const nodesByName = store.engine!.getModel().getNodes().filter((node: any) => node.schema?.name === nodeName).map((n: any) => n.options.name);
    let nodesCount = nodesByName.length;
    do {
      let name = nodeName + "-" + nodesCount;
      if (!nodesByName.includes(name)) {
        return name;
      }
      nodesCount++;
    } while (true);
  };
  const generateRandomString = (): string => {
    return `a${Math.random().toString(36).slice(2)}`;
  };

  const generateRandomUUID = () => {
    const allNodesIds = NodeIdsList();
    let uuid: string;
    do {
      uuid = generateRandomString();
    } while (allNodesIds.includes(uuid));

    return uuid;
  }

  const ondrop = (event: any, catalog: NodeDefinitions) => {
    if (!store.engine) {
      console.error("failed to drop item. diagram is empty!");
      return;
    }

    let data = JSON.parse(
      event.dataTransfer.getData("storm-diagram-node")
    ) as NodeDefinition;

    if (!event.dataTransfer.getData("storm-diagram-node")) {
      return;
    }

    let options;
    if (data.kind === NodeKind.NOTE) {
      const nodeDefinition : NodeDefinition = {
        name: 'Note',
        kind: NodeKind.NOTE,
        type: 'sticky-note',
        description: 'Notes',
        settings: {}
      }

      options = {
        schema: nodeDefinition,
        settings: {},
        type: "note",
        id: generateRandomUUID(),
      };
    } else {
      let values: SettingsValues = {};
      for (const key in data.settings) {
        values[key] = data.settings[key]?.default_value;
      }

      if (data.runtimeMode === RuntimeMode.BASIC && !data.noDefaultProperties) {
        const basicProperties = config?.runtimeMode.find(runtime => runtime.name === RuntimeMode.BASIC);
        if (basicProperties?.properties) {
          basicProperties.properties.forEach((property: { key: string | number; value: any; }) => {
            if (!values[property.key]) {
              values[property.key] = property.value;
            }
          })
        }
      }

      const s = getSchema(data.name, data.kind, catalog);
      if (s !== undefined) {
        const brickName = generateRandomName(data.name);

        options = {
          schema: s,
          name: brickName,
          type: data.kind,
          isDeprecated: data.isDeprecated,
          settings: values,
          id: generateRandomUUID()
        };
      } else {
        console.error(
            "fail to add node, definition not found!!! node:",
            data.name,
            data.kind,
            data.type
        );
      }
      dispatch(setFilter(""));
    }

    if (options) {
      const node = new DataNodeModel(options);
      node.setPosition(store.engine.getRelativeMousePoint(event));
      store.engine.getModel().addNode(node);

      store.activeNode = node;
      dispatch(
          changeSidebarPane({ pane: "configuration", arg: node.getID() })
      );
      store.engine.repaintCanvas();
    }
  };

  const createModel = () => {
    const model = new DiagramModel();
    model.setGridSize(10);
    return model;
  };

  const save = (options?: {
    removeDeadLink?: boolean;
    ignoreSelected?: boolean;
  }): string => {
    if (!store.engine) {
      return "";
    }
    // Default values for `options`
    options = {
      removeDeadLink: true,
      ignoreSelected: true,
      ...(options || {}),
    };

    const model = store.engine.getModel();

    if (options.removeDeadLink) {
      removeLinkWithoutTargetPort(model);
      store.engine.repaintCanvas();
    }

    const graph = model.serialize();
    if (options.ignoreSelected) {
      // For object (node, links...) in each layer, set `selected` to false
      // (because that's not something interesting to save)
      for (const layer of graph.layers) {
        for (const modelId in layer.models) {
          layer.models[modelId].selected = false;
        }
      }
    }
    return JSON.stringify(graph);
  };


  const load = async (serializedModel: string, catalog: NodeDefinitions) => {
    store.engine = createEmptyDiagram();
    if (!store.engine) return;

    const model = new DiagramModel();

    try {
      model.deserializeModel(JSON.parse(serializedModel), store.engine);
      const missingUdf: string[] = [];

      model.getNodes().forEach((n) => {
        if (n instanceof DataNodeModel && n.kind !== NodeKind.NOTE) {
          const sc = getSchema(n.getTechnicalName(), n.getKind(), catalog);
          if (sc) {
            n.setDefinition(sc);
          } else {
            const deletedMatch = deletedBoxes.find(
              (d) =>
                d.technicalName === n.getTechnicalName() &&
                d.kind.toLowerCase() === n.getKind().toLowerCase()
            );

            if (deletedMatch) {
              const deletedNode = createDeletedBoxNode(n, deletedMatch.message ?? "");
              model.removeNode(n);
              model.addNode(deletedNode);
              if (deletedNode.schema) {
                n.setDefinition(deletedNode.schema);
              }
            } else {
              missingUdf.push(n.getTechnicalName());
            }
          }
        }
      });

      if (missingUdf.length > 0) {
        dispatch(
          setToast({
            content:
              "Impossible de lancer ce traitement car les briques suivantes sont manquantes: " +
              missingUdf.join(", "),
            life: 6000,
            severity: "error",
            title: "Erreur",
          })
        );
        store.engine = undefined;
        navigate(`/treatments`);
      } else {
        model.serialize();
        store.engine.setModel(model);
      }
    } catch (error) {
      console.error("Deserialization error:", error);
    }
  };
  const createDeletedBoxNode = (originalNode: DataNodeModel, deleteMessage: string): DataNodeModel => {
    const emptyCondition: Condition = { keyName: "", values: [] };

    const originalInputs: PortDefinition[] = Object.values(originalNode.getPorts())
      .filter((p: any) => p.in && !p.isSidePort)
      .map((p: any, idx) => ({
        id: p.id ?? `in_${idx}`,
        name: p.getName(),
        type: "any",
        actions: [],       // pas d'actions sur une boîte supprimée
        fields: [],
        no_render_condition: emptyCondition
      }));

    const originalOutputs: PortDefinition[] = Object.values(originalNode.getPorts())
      .filter((p: any) => !p.in && !p.isSidePort)
      .map((p: any, idx) => ({
        id: p.id ?? `out_${idx}`,
        name: p.getName(),
        type: "any",
        actions: [],
        fields: [],
        no_render_condition: emptyCondition
      }));

    const originalSideouts: SideOutPortDefinition[] = Object.values(originalNode.getPorts())
      .filter((p: any) => !p.in && p.isSidePort)
      .map((p: any, idx) => ({
        id: p.id ?? `side_${idx}`,
        name: p.getName(),
        schema: "" // obligatoire selon ton type
      }));
      const def: NodeDefinition = {
        name: "DELETED-BOX",
        type: "DELETED-BOX",
        kind: NodeKind.DELETED,
        author: "Athea",
        description: `Brique supprimée : ${originalNode.getTechnicalName()}`,
        icon: originalNode.schema?.icon,
        isDeprecated: false,
        noDefaultProperties: true,
        brut: false,

        input: originalOutputs,
        output: originalInputs,
        sideout: originalSideouts,

        settings: {
          name: {
            name: "Nom de la brique supprimée",
            type: "string",
            default_value: originalNode.getTechnicalName()
          },
          message: {
            name: "Information",
            type: "string",
            default_value: deleteMessage
          }
        },

        tabs: [
          {
            name: "Info",
            fields: ["name","message"]
          }
        ]
      };

      const placeholder = new DataNodeModel({
        id: originalNode.getID(), // même ID pour garder les liens !
        name: "DELETED-BOX",
        settings: { name: originalNode.getTechnicalName(), message: deleteMessage },
        schema: def,
        isDeprecated: false,
      });

      placeholder.setPosition(originalNode.getX(), originalNode.getY());

      return placeholder;
  };
  const reroute = () => {
    if (!store.engine) {
      return;
    }
    const model = store.engine.getModel();
    let dagreEngine = new DagreEngine({
      graph: {
        rankdir: "LR",
        ranker: "longest-path",
        marginx: 25,
        marginy: 25,
      },
      includeLinks: false
    });
    dagreEngine.redistribute(model);
    store.engine.repaintCanvas();
  };

  const zoomIn = () => {
    if (store.engine) {
      store.engine
        .getModel()
        .setZoomLevel(store.engine.getModel().getZoomLevel() + 10);
      store.engine.repaintCanvas();
    }
  };

  const reRoute = () => {
    if (store.engine) {
      dispatch(
        setDialog({
          content: dialogContent(),
          title: translate("button.order")
        })
      );
    }
  };

  const dialogContent = () => {
    return (
      <>
        <div className="mb-3">
          {translate("label.reordonateMessage")}
          <span> {translate("label.reordonateWarning")} </span>
        </div>
        {footerDialog()}
      </>
    );
  };

  const footerDialog = () => {
    return (
      <div style={{ float: "right" }}>
        <Button
          label={translate("button.cancel").toUpperCase()}
          onClick={() => dispatch(setDialog(undefined))}
          className="mr-3 p-button-outlined p-button-sm"
        />

        <Button
          id="hook-form"
          onClick={() => {
            reroute();
            dispatch(setDialog(undefined))
          }}
          type="submit"
          label={translate("button.reordonner").toUpperCase()}
          className="p-button-danger p-button-sm"
        ></Button>
      </div>
    );
  };

  const zoomOut = () => {
    /* Min zoom Level to see always the graph on the editor even is small */
    if (store.engine && store.engine.getModel().getZoomLevel() >= 40) {
      store.engine
        .getModel()
        .setZoomLevel(store.engine.getModel().getZoomLevel() - 10);
      store.engine.repaintCanvas();
    }
  };
  // const convertStringToBoolean = (value)

  // stream flink

  // return if exist the value of the node settings  wanted
  const valueSettingsNode = (
    idNode: string,
    keyName: String
  ): string | undefined => {
    let valueOfSettingNode = undefined;
    if (store.engine) {
      store.engine
        .getModel()
        .getNodes()
        .every((n: NodeModel) => {
          /*  every is used to eskape the loop when false is returned */
          const node = n as DataNodeModel;
          if (
            idNode === node.getOptions()?.id &&
            // @ts-ignore
            node.settings?.[keyName] !== undefined &&
            // @ts-ignore
            node.settings?.[keyName] !== null &&
            // @ts-ignore
            node.settings?.[keyName] !== ""
          ) {
            // @ts-ignore
            valueOfSettingNode = node.settings?.[keyName];

            return false;
          }
          return true;
        });
    }
    return valueOfSettingNode;
  };

  const getNodeById = (nodeID: string): DataNodeModel | null => {
    try {
      /* @ts-ignore */
      return store.engine?.getModel().getNode(nodeID);
    } catch {
      return null;
    }
  };

  const getLowLevelNodes = (
    dataNode: DataNodeModel,
    hidePassword: boolean,
    catalog: NodeDefinitions
  ): DagItem[] => {
    let schema = dataNode.schema;
    if (schema === undefined) {
      console.error(
        "fail to get definition when rendering low level node ",
        dataNode
      );
      return [];
    }

    const values = dataNode.settings;

    const helpers = {
        // Start interpolation process
        /**
         * Function that parses any JSON object and replaces in any string the placeholder
         * Support placeholder pattern is ${xxx}
         * Placeholder is replaced by value found in dataNode.settings
         * Ex: ${username} will be replaced by the value found in dataNode.setting.username
         * If no value is found, placeholder is not replaced
         * @param (object) properties (JSON Object)
         * @returns (object) JSON interpolated value
         */
        interpolate: (properties: any, numberofremplace: number = 0): any => {
            if (numberofremplace > 100) {
              dispatch(
                setToast({
                    severity: 'error',
                    title: translate("label.interpolateErrorMessageTitle"),
                    content: translate("label.interpolateErrorMessageContent"),
                    life: 3000,
                })
              );
              throw new Error("Interpolation limit exceeded: possible infinite loop.");
            }
            if (!properties) {
                return properties;
            }
            if (typeof properties === "string") {
                const matches: string[] | null = properties.match(placeholderRegex);
                if (matches) {
                    let parsedValue = properties;
                    for (const match of matches) {
                        numberofremplace++
                        const keyToGetValueFrom = (new RegExp(placeholderRegex)).exec(match)?.[1]?.trim();
                        if (
                            keyToGetValueFrom &&
                            (values || schema?.settings?.[keyToGetValueFrom]?.default_value !== undefined)
                        ) {
                            // Replace placeholders
                            let newValue = helpers.get_value(keyToGetValueFrom, numberofremplace);
                            if (newValue === undefined)
                                newValue = match;
                            if (parsedValue === match && typeof newValue !== "string") { // special case which enables whole object replacements, second condition to keep supporting text remplace with no string interpolation
                                parsedValue = newValue;
                            } else {
                                parsedValue = parsedValue.replace(match, newValue);
                            }
                        }
                    }
                    if (parsedValue !== properties) {
                        return helpers.interpolate(parsedValue, numberofremplace); // further interpolation may be needed
                    }
                } 
                return properties;
            }
            if (typeof properties === "object" && Array.isArray(properties)) {
                let result = [];
                for (const item of properties) {
                    result.push(helpers.interpolate(item, numberofremplace));
                }
                return result;
            }
            if (typeof properties !== "object") {
                return properties;
            }
            const returnObject: any = {};
            for (const key in properties) {
                let propertyValue = properties[key];
                if (key === "dependencies") {
                    // We don't want to interpolate dependencies properties because they have another process below
                    returnObject[key] = propertyValue;
                    continue;
                }
                returnObject[key] = helpers.interpolate(propertyValue, numberofremplace);
            }
            return returnObject;
        },

        get_value : (key: string, numberofremplace: number = 0): any => {
            let value = values?.[key]; /* user provided value for same attribute */
            let keySettings = schema?.settings[
                key
            ]; /* schema definition, default, type, required... */

            if (
                (value === undefined || value === null || value === "") &&
                (keySettings === undefined || keySettings.default_value === undefined ||
                keySettings.default_value === null ||
                keySettings.default_value === "") &&
                (keySettings === undefined || !keySettings.dependencies)
            ) {
                console.warn(
                `missing value, default value and dependencies for field ${key} in node ${dataNode.getName()} (#${dataNode.getID()})`
                );
                return undefined;
            }

            let typeOfField: string = keySettings?.type || "string";

            if (
                typeOfField.startsWith("multichoice") ||
                typeOfField === "multipleavro"
            ) {
                typeOfField = "list<string>";
            }

            if (!keySettings?.dependencies) {
                switch (typeOfField) {
                case "booleanstring":
                    return value === true || value === false
                        ? value
                            ? "true"
                            : "false"
                        : keySettings?.default_value === "true" ||
                        keySettings?.default_value === "false"
                        ? keySettings.default_value
                        : "false";
                case "boolean":
                    return value === true || value === false
                        ? value
                        : keySettings?.default_value?.toString().toLowerCase() ===
                            "true";
                case "number":
                    let updatedValue = keySettings?.default_value;
                    if (value) {
                        updatedValue = Number.isInteger(value)
                            ? parseInt(value)
                            : parseFloat(value);
                    }
                    return updatedValue;
                case "list<string>":
                    return value !== undefined && value !== null
                        ? value
                        : keySettings?.default_value;
                case "map":
                    return value !== undefined && value !== null ? value : keySettings?.default_value !== undefined ? keySettings.default_value : {};
                case "password":
                    return hidePassword
                        ? "**********"
                        : value !== undefined && value !== null
                        ? value
                        : keySettings?.default_value;
                default:
                    let safeValue = value ? value : keySettings?.default_value;
                    if (typeof safeValue === "string") {
                        let vkeys: string[] = Object.keys(values || {});
                        vkeys.forEach((k: string) => {
                            safeValue = safeValue.replaceAll(
                                `$\{settings.${k}}`,
                                values?.[k] ? values[k] : `$\{settings.${k}}`
                            );
                        });
        
                        // other node replacement
                        safeValue = safeValue.replaceAll(`$\{id}`, dataNode.getID());
                        safeValue = safeValue.replaceAll(
                            `$\{name}`,
                            dataNode.getName()
                        );
                        safeValue = safeValue.replaceAll(
                            `$\{type}`,
                            dataNode.getTechnicalType()
                        );
                        safeValue = safeValue.replaceAll(
                            `$\{kind}`,
                            dataNode.getKind()
                        );
                    }
                    dataNode.getInPorts().forEach((p) => {
                        Object.keys(p.getLinks()).forEach((linkId: string) => {
                            const link: NodeLinkModel = p.getLinks()[
                                linkId
                            ] as NodeLinkModel;
                            const srcNode = link
                                .getSourcePort()
                                .getNode() as DataNodeModel;
                            if (typeof safeValue === "string") {
                                safeValue = safeValue.replaceAll(
                                    `$\{in.${p.getName()}}`,
                                    slugify(srcNode.getID())
                                );
                            }
                        });
                    });
                    if (
                        (typeof safeValue === "string" && safeValue.trim() !== "") ||
                        typeof safeValue !== "string"
                    ) {
                      return helpers.interpolate(safeValue, numberofremplace+1); // there may still be placeholders to replace
                    } else {
                        return undefined;
                    }
                }
            }
            // Check if dependencies property exist
            if (keySettings.dependencies) {
                for (let dependency of keySettings.dependencies) {
                    for (let dependencyValue of dependency.values) {
                        const currentKeyValue = helpers.get_value(dependency.keyName);
                        if (
                            currentKeyValue === dependencyValue.value ||
                            (dependencyValue.value === false &&
                                currentKeyValue === undefined)
                        ) {
                            // Replace string process
                            if (dependencyValue.replace_value) {
                                return helpers.interpolate(dependencyValue.replace_value) || "";
                            }
                        }
                    }
                }
            }
            return undefined;
        },
    };

    // Replace current schema by the interpolated one
    try {
        schema = helpers.interpolate(schema);
      } catch (error) {
        if(store.engine) {
          dataNode.remove();
          store.engine.repaintCanvas();
        }
        return []
    }

    let attributes: { [key: string]: any } = {};
    if (schema) {
      if (schema.brut && values) {
        const value = values["schema"];
        if (value) {
          try {
            const jsonBrut = JSON.parse(value);
            attributes = {...attributes, ...jsonBrut}
          } catch (e) {
            console.error('Erreur lors du parsing JSON de la brique :', "" + dataNode.getOptions().name);
          }
        }
      } else if (schema.settings && values) {
        for (const initkey in schema.settings) {

          const  key = [...initkey.matchAll(/\w+|\[(.*?)\]/g)].map(match => match[1] ?? match[0]);
          const value = helpers.get_value(initkey);

          // Définition du type d'un champ de settings
          type FieldSetting = typeof schema.settings[keyof typeof schema.settings];

          // Récupération du setting du champ
          const keySettings: FieldSetting | undefined = schema?.settings?.[initkey];

          // skip if file is not rendered
          if (keySettings?.no_render) {
              continue;
          }

          // here no_render is either false or undefined. If it is false, then it means we explicitely want to render it, we render the field whatever the condition, which is then a display condition and not a rendering condition
          if (keySettings.no_render === undefined && keySettings.condition) {


            const conditionKey = keySettings.condition.keyName;
            const nodeId = dataNode.getOptions().id;
            const KeyNameValue = valueSettingsNode(
                nodeId ?? "",     // fallback string pour TS
                conditionKey
            );
            const conditionResult = CheckConditionValue(
                KeyNameValue,
                keySettings.condition.values,
                schema?.settings[conditionKey]?.default_value
            );
            if (!conditionResult) {
                continue;
            }
          }
          if (value !== undefined) {
              _.set(attributes, key, value);
          }
        }
      }
    }

    // asStream/asTable
    if (values && values["asTable"]) {
      _.set(attributes, "asTable", values["asTable"]);
    }

    // deal with out
    const out: string[] = [];
    for (let i = 0; i < dataNode.getDataOutPorts().length; i++) {
      const outLinks = dataNode.getDataOutPorts()[i].getLinks();
      const outLinkNames = Object.keys(outLinks);

      for (const outLinkName of outLinkNames) {
        const outId = outLinks[outLinkName]
          .getTargetPort()
          ?.getParent()
          .getID();
        const outName = outLinks[outLinkName].getSourcePort().getOptions().name;
        const renderConditionValue = schema?.output?.find(
          (value) => value.name === outName
        )?.no_render_condition;
        let checkIfOutCanBeRendered: boolean = false;
        if (renderConditionValue) {
          let KeyNameValue = valueSettingsNode(
            // @ts-ignore
            dataNode.getOptions().id,
            renderConditionValue.keyName
          );
          if (
            CheckConditionValue(
              KeyNameValue,
              renderConditionValue.values,
              schema?.settings?.[renderConditionValue.keyName]?.default_value
            )
          ) {
            checkIfOutCanBeRendered = true;
          }
        }

        const type = outLinks[outLinkName].getTargetPort();
        // if link has no target, `type` is undefined
        if (!checkIfOutCanBeRendered) {
          if (type) {
            /* @ts-ignore */
            if (type.technicalType === "coprocess") {
              const entree: any = outLinks[outLinkName]
                .getTargetPort()
                ?.getParent();
              if (entree.ports) {
                const ports = Object.keys(entree.ports);
                for (let i = 0; i < ports.length; i++) {
                  if (entree.ports?.[ports[i]]?.links?.[outLinkName]) {
                    out.push(`${outId}::${i + 1}`);
                  }
                }
              }
            } else {
              out.push(outId);
            }
          }
        }
      }
    }

    // deal with sideout
    let sideout:
      | { [key: string]: { out: string; schema: string } }
      | undefined = undefined;
    for (let i = 0; i < dataNode.getSideOutPorts().length; i++) {
      const sideport = dataNode.getSideOutPorts()[i];
      const name = sideport.getName();
      const outLinks = sideport.getLinks();
      const outLinkNames = Object.keys(outLinks);
      for (const outLinkName of outLinkNames) {
        const outId = outLinks[outLinkName]
          .getTargetPort()
          ?.getParent()
          .getID();
        if (sideout === undefined) {
          sideout = {};
        }

        const dSide = sideport.dynamicSideout
          ? dataNode.getSideOut(sideport.getName())
          : dataNode.getSideOut(sideport.getOutId());
        const def = sideport.getDefinition();
        let schema = "";
        if (dSide && dSide.schema) {
          try {
            schema = JSON.stringify(JSON.parse(dSide.schema));
          } catch (e) {
            console.error("INVALID JSON SCHEMA", dSide);
          }
        } else if (def) {
          try {
            schema = JSON.stringify(JSON.parse(def.schema));
          } catch (e) {
            console.error("INVALID JSON SCHEMA", dSide);
          }
        }
        sideout[
          sideport.dynamicSideout ? sideport.getName() : sideport.getOutId()
        ] = {
          out: outId,
          schema: schema,
        };
      }
    }

    let node: any = {
      id: dataNode.getID(),
      type: schema?.type || dataNode.getTechnicalType(),
      ...attributes,
      out,
    };

    if (schema?.code) {
      node.code = schema.code;
    }

    if (sideout) {
      node.sideOut = sideout;
    }

    if (schema?.code) {
      node.code = schema?.code
    }
    return [node];
  };

  const nodeInformations = (list: string) => {
    if (!store.engine) {
      return [];
    }
    let valueNodes: any = [];
    store.engine
      .getModel()
      .getNodes()
      .forEach((n: NodeModel) => {
        const runtime: any = n;
        if (runtime.runtime) {
          valueNodes = valueNodes.concat(runtime.runtime);
        }
      });
    if (list === "list") {
      const uniqueNames = Array.from(new Set(valueNodes)).map((i) => ({
        name: i,
      }));
      return uniqueNames;
    }
    return Array.from(new Set(valueNodes));
  };

  const NodeIdsList = (): string[] => {
    if (!store.engine) {
      return [];
    }

    const nodeIdsList: string[] = [];
    store.engine
      .getModel()
      .getNodes()
      .forEach((n) => {
        const nodeId = n.getOptions().id;
        if (nodeId) {
          nodeIdsList.push(nodeId);
        } else {
          console.warn("[AllNodes] Node with undefined id", n);
        }
      });

    return nodeIdsList;
  };

  const getNodesByKind = (kind: string): DataNodeModel[] => {
    const nodes: DataNodeModel[] = [];
    const nodeIds = NodeIdsList();
    nodeIds.forEach((nodeId) => {
      const node = getNodeById(nodeId);
      if (node && node.kind === kind) {
        nodes.push(node);
      }
    });
    return nodes;
  };

  const getNodesFromPort = (port: DefaultPortModel): DataNodeModel[] => {
    const nodes: DataNodeModel[] = [];
    const links = port.getLinks();
    Object.values(links).forEach((link) => {
      const sourcePort = link.getSourcePort();
      const node = sourcePort.getNode();
      nodes.push(node as DataNodeModel);
    });
    return nodes;
  }

  const getPreviousNodes = (node: DataNodeModel): {name: string, nodes: DataNodeModel[]}[] =>  {
    const retVal:{name: string, nodes: DataNodeModel[]}[] = [];
    const ports = node.getInPorts();
    ports.forEach((port) => {
      retVal.push({
        name: port.getName(),
        nodes: getNodesFromPort(port as DefaultPortModel)
      });
    });
    return retVal;
  }

  const getFollowingNodes = (node: DataNodeModel): {name: string, nodes: DataNodeModel[]}[] =>  {
    const retVal:{name: string, nodes: DataNodeModel[]}[] = [];
    const ports = node.getOutPorts();
    ports.forEach((port) => {
      retVal.push({
        name: port.getName(),
        nodes: getNodesFromPort(port)
      });
    });
    return retVal;
  }

  const udpateActiveNode = (activeNode: DataNodeModel | undefined) => {
    store.activeNode = activeNode;
    store.engine?.repaintCanvas();
  }

  return {
    zoomIn,
    reRoute,
    zoomOut,
    reroute,
    getPreviousNodes,
    getFollowingNodes,
    getNodesFromPort,
    getNodesByKind,
    engine: store.engine,
    isReady: store.engine !== undefined,
    activeNode: store.activeNode,
    newDiagram: () => {
      store.engine = createEmptyDiagram();
    },

    deleteDiagram: () => {
      store.engine = undefined;
      store.activeNode = undefined;
    },
    udpateActiveNode,
    deleteByItem: () => {
      if (store.engine) {
        const selectedEntities = store.engine.getModel().getSelectedEntities();
        if (selectedEntities.length > 0) {
          const confirm = window.confirm("Voulez-vous supprimer ce composant?");
          if (confirm) {
            selectedEntities.map((model) => {
              if (!model.isLocked()) {
                // @ts-ignore
                model.remove();
              }
            });
            store.engine.repaintCanvas();
          }
        }
      }
    },
    copyItem: () => {
      if (!store.engine) {
        console.error("failed to duplicate object!");
        return;
      }

      const selectedEntities = store.engine.getModel().getSelectedEntities();
      if (selectedEntities.length > 0) {
        const confirm = window.confirm("Voulez-vous dupliquer ce composant?");
        if (confirm) {
          selectedEntities.forEach((model) => {
            const node = getNodeById(model.getOptions().id!);

            if (node) {
              const options: DataNodeModelOptions = {
                schema: node.schema,
                name: generateRandomName(node.schema!.name),
                type: node.kind,
                settings: node.settings,
                id: generateRandomUUID(),
              };

              const newNode = new DataNodeModel(options);
              const position = node.getPosition();
              const point = new Point(position.x + 10, position.y + 10);
              newNode.setPosition(point);
              store.engine!.getModel().addNode(newNode);
            }
          });
          store.engine.repaintCanvas();
        }
      }
    },

    clearDiagram: () => {
      if (!store.engine) {
        console.error("No diagram to clear.");
        return;
      }
      store.engine.setModel(createModel());
    },
    zoomToFitNodes: () => {
      if (!store.engine) {
        console.error("no diagram to zoom.");
        return;
      }
      store.engine.zoomToFitNodes({margin: 50, nodes: store.engine.getModel().getNodes()});
    },
    zoomFit: () => {
      if (!store.engine) {
        console.error("no diagram to zoom.");
        return;
      }
      store.engine.zoomToFit();
    },
    getNodeById,
    handleDrop: ondrop,
    save: save,
    load: load,
    setRef: (ref: any) => {
      store.ref = ref;
    },
    nodeInformations,
    render: ({
      hidePassword,
      catalog,
    }: {
      hidePassword: boolean;
      catalog: NodeDefinitions;
    }): DagItem[] | undefined => {
      if (!store.engine) {
        console.error("failed to render graph. engine is undefined!");
        return undefined;
      }

      // tasks is sidecar image to attach
      const tasks: any[] = [];
      // main dag
      const dag: DagItem[] = [];
      const nodesIdDisabled:string[] =  []
      // 1/ check if is a stream or sql dag
      store.engine
        .getModel()
        .getNodes()
        .forEach((n: NodeModel) => {
          const node = n as DataNodeModel;
          if (node.kind !== NodeKind.NOTE) {
            if (node.isDisabled) {
                nodesIdDisabled.push(node.getID())
                return
            }

            if (node.schema) {
              if (node.schema.settings && node.schema.settings["asStream"]) {
                // set all in node 'asTable'
                for (const inPort of node.getInPorts()) {
                  const linkIds = Object.keys(inPort.getLinks());
                  for (const linkId of linkIds) {
                    // @ts-ignore
                    const link = store.engine.getModel().getLink(linkId);
                    const parent = link
                        .getSourcePort()
                        .getParent() as DataNodeModel;
                    if (parent.settings) {
                      parent.settings["asTable"] = true;
                    }
                  }
                }
              }

              if (node.schema.containers) {
                // set all in node 'asTable'
                for (const container of node.schema.containers) {
                  tasks.push({
                    id: `${container.name}-${node.getID()}`,
                    image: container.image,
                    // command container.command,
                    // args    container.args,
                  });
                }
              }
            }
            dag.push(
                ...getLowLevelNodes(node as DataNodeModel, hidePassword, catalog)
            );
          }
        });

        for (let item of dag) {
          if (item.out) {
            let finalOut:string[] = []
            for (let out of item.out) {
              if (!nodesIdDisabled.includes(out)) {
                finalOut.push(out)
              }
            }
            item.out = finalOut
          }
        }

      return dag;
    },
  };
};
